import { SessionUser } from 'types/game';
import { formatCurrencyAmount, currencySymbolForCode } from 'lib/currency-format';

/** Playable cash — for consumer this is the only balance field (chips at table or wallet). */
export function getFreeBalance(user: SessionUser | null): number {
  if (!user) return 0;
  return Math.max(0, Math.floor(Number(user.balance) || 0));
}

/** @deprecated Consumer uses balance only; kept for legacy non-consumer sessions. */
export function getSpendableWalletBalance(user: SessionUser | null): number {
  return getFreeBalance(user);
}

/** True when the player still has chips seated at this table profile (rejoin without buy-in). */
export function hasActiveTableChips(user: SessionUser | null, tableProfileId: number): boolean {
  if (!user || tableProfileId <= 0) return false;
  const activeId = Math.floor(Number(user.activeTableProfileId) || 0);
  if (activeId !== tableProfileId) return false;
  const stack = Math.max(0, Math.floor(Number(user.tableStack) || 0));
  return stack > 0;
}

/**
 * Single balance for UI — consumer: `user.balance` or live stack at table;
 * legacy wallet: free balance + table stack.
 */
export function getPlayableBalance(user: SessionUser | null, liveTableStack?: number): number {
  if (!user) return 0;
  if (user.consumerWallet) {
    if (liveTableStack != null) {
      return Math.max(0, Math.floor(Number(liveTableStack) || 0));
    }
    return getFreeBalance(user);
  }

  const wallet = getFreeBalance(user);
  const locked = Math.max(0, Math.floor(Number(user.lockedBalance) || 0));
  const tableStack =
    liveTableStack != null
      ? Math.max(0, Math.floor(Number(liveTableStack) || 0))
      : Math.max(0, Math.floor(Number(user.tableStack) || 0));

  if (tableStack > 0) return wallet + tableStack;
  return wallet + locked;
}

/** @deprecated Use getPlayableBalance */
export function getConsumerPlayableBalance(
  user: SessionUser | null,
  liveTableStack?: number
): number {
  return getPlayableBalance(user, liveTableStack);
}

/** @deprecated Use getPlayableBalance */
export function getTotalWealth(user: SessionUser | null, liveTableStack?: number): number {
  return getPlayableBalance(user, liveTableStack);
}

/** @deprecated Use getPlayableBalance */
export function getDisplayBalance(user: SessionUser | null, liveTableStack?: number): number {
  return getPlayableBalance(user, liveTableStack);
}

/** @deprecated Use getPlayableBalance with liveTableStack */
export function getInTableHeaderBalance(user: SessionUser | null, liveTableStack: number): number {
  return getPlayableBalance(user, liveTableStack);
}

/** Header / lobby — one balance only. */
export function getHeaderBalance(user: SessionUser | null): number {
  return getPlayableBalance(user);
}

export function formatBalanceAmount(
  user: SessionUser | null,
  liveTableStack?: number,
  options?: Intl.NumberFormatOptions
): string {
  const symbol = currencySymbolForCode(user?.currency, user?.currencySymbol);
  const amount = getPlayableBalance(user, liveTableStack);
  return formatCurrencyAmount(symbol, amount, user?.currency, options);
}

export function formatFreeBalanceAmount(
  user: SessionUser | null,
  options?: Intl.NumberFormatOptions
): string {
  return formatBalanceAmount(user, undefined, options);
}

export function formatWalletBreakdown(
  user: SessionUser | null,
  liveTableStack?: number
): { main: string; sub?: string } {
  const symbol = currencySymbolForCode(user?.currency, user?.currencySymbol);
  const wallet = getFreeBalance(user);
  const onTable =
    liveTableStack != null
      ? Math.max(0, Math.floor(Number(liveTableStack) || 0))
      : Math.max(0, Math.floor(Number(user?.tableStack) || 0));

  if (user?.consumerWallet) {
    const amount = liveTableStack != null ? onTable : wallet;
    return { main: formatCurrencyAmount(symbol, amount, user?.currency) };
  }

  if (onTable > 0) {
    return {
      main: formatBalanceAmount(user, onTable),
      sub: `Wallet ${formatCurrencyAmount(symbol, wallet, user?.currency)} · On table ${formatCurrencyAmount(symbol, onTable, user?.currency)}`,
    };
  }

  return { main: formatFreeBalanceAmount(user) };
}
